/**
 * What's New Content - Version-keyed feature announcements (DD-160)
 *
 * Each entry represents a release with notable features, improvements,
 * and fixes. The WhatsNewModal displays the latest entry to returning
 * users who have been inactive for >7 days.
 *
 * Content is keyed by semantic version string. The modal shows the
 * highest version the user hasn't dismissed yet.
 *
 * Keep entries concise: 3-5 items per release maximum.
 */

export interface WhatsNewItem {
  /** Emoji icon for the feature card */
  icon: string;
  /** Short title (max ~40 chars) */
  title: string;
  /** One-line description (max ~80 chars) */
  description: string;
  /** Category for analytics and styling */
  category: 'feature' | 'improvement' | 'fix';
  /** Optional deep link route within the app */
  route?: string;
}

export interface WhatsNewRelease {
  /** Semantic version key */
  version: string;
  /** Release headline shown at top of modal */
  headline: string;
  /** Individual feature/improvement/fix items */
  items: WhatsNewItem[];
}

/**
 * All releases, newest first.
 * The modal shows the first entry whose version > user's last dismissed version.
 *
 * Content is keyed by version — i18n would require a hook context.
 * Use defaultValue pattern in WhatsNewModal instead.
 */
export const WHATS_NEW_RELEASES: WhatsNewRelease[] = [
  {
    version: '2.4.0',
    headline: "Here's what you missed",
    items: [
      {
        icon: '\u{1F4AC}',
        title: 'Group Conversations',
        description: 'Analyze group chats with multiple participants at once',
        category: 'feature',
        route: '/(tabs)',
      },
      {
        icon: '\u{1F4CA}',
        title: 'Pattern Trends',
        description: 'See how communication patterns evolve over time',
        category: 'feature',
        route: '/(tabs)/stats',
      },
      {
        icon: '\u{26A1}',
        title: 'Faster Analysis',
        description: 'Results now arrive up to 40% faster',
        category: 'improvement',
      },
      {
        icon: '\u{1F512}',
        title: 'Enhanced Privacy',
        description: 'Messages are now encrypted end-to-end during analysis',
        category: 'improvement',
      },
    ],
  },
  {
    version: '2.3.0',
    headline: "What's new in DramaDetector",
    items: [
      {
        icon: '\u{1F30D}',
        title: 'Spanish Language Support',
        description: 'Full app translation now available in Spanish',
        category: 'feature',
      },
      {
        icon: '\u{1F3A8}',
        title: 'Refreshed Design',
        description: 'New glassmorphic UI with smoother animations',
        category: 'improvement',
      },
      {
        icon: '\u{1F41B}',
        title: 'Bug Fixes',
        description: 'Resolved issues with history sync and quota display',
        category: 'fix',
      },
    ],
  },
];

/**
 * Get the latest release version string.
 */
export function getLatestVersion(): string {
  return WHATS_NEW_RELEASES[0]?.version ?? '0.0.0';
}

/**
 * Get the release entry for a specific version.
 */
export function getReleaseByVersion(version: string): WhatsNewRelease | undefined {
  return WHATS_NEW_RELEASES.find((r) => r.version === version);
}
